import { isAnyRealEstateCategory } from '@kijiji/category'
import { type AttributeValue, ResultsView } from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'

import { TreeFilter } from '@/components/srp/filters/filter-types/TreeFilter'
import { type TreeFilterProps } from '@/components/srp/filters/FiltersAccordion/FiltersAccordion'
import { getDescriptionFromSelectedValues } from '@/domain/srp/filters/getDescriptionFromSelectedValues'
import { getCategoryLevel } from '@/domain/srp/getCategoryLevel'
import { getBoundingBoxFromRadius } from '@/features/map/utils/getBoundingBoxFromRadius'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { getSearchViewType } from '@/features/search/utils/getSearchViewType'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { RemoteToggles } from '@/lib/firebase/config/toggles'
import { RemoteParamKeys, useExperiment, useToggle } from '@/lib/firebase/hooks'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { AccordionItem } from '@/ui/atoms/accordion'

export const CategoryTreeFilter: FC<TreeFilterProps> = ({
  filter: categoryTree,
  selectedId: categoryId,
  refetch: refetchResults,
  onClickAccordionItem,
}) => {
  const { viewType, updatePageViewType } = useGetSrpState()
  const { t } = useTranslation('srp')
  const { location } = useGetLocation()

  const isSRPGridToggleEnabled = useToggle(RemoteToggles.SRP_GRID_VIEW_TOGGLE.key)?.enabled

  const realEstateGridExp = useExperiment(RemoteParamKeys.SRP_REAL_ESTATE_GRID_TEST)
  const isRealEstateGridExpTestGroup = realEstateGridExp?.enabled && realEstateGridExp?.group === 1

  const description = getDescriptionFromSelectedValues(
    categoryTree?.selectedValues,
    categoryTree?.values
  )

  const handleSelectCategory = (categoryLeaf: AttributeValue) => {
    const selectedCategoryId = parseInt(categoryLeaf.value)

    if (selectedCategoryId === categoryId) return

    const categoryLevel = getCategoryLevel({ categoryId: selectedCategoryId })
    const trackingLabel = `cat${categoryLevel}=${selectedCategoryId}`

    /**
     * It should refetch listings based on the bounding box if the selected category is within "Real Estate"
     * AND the selected view is MAP (which is only allowed when the selected location is an area)
     */
    const shouldPopulateBoundingBox =
      isAnyRealEstateCategory(selectedCategoryId) && viewType === ResultsView.Map

    let boundingBox = undefined
    if (shouldPopulateBoundingBox && location.area) {
      const { latitude, longitude, radius } = location.area
      boundingBox = getBoundingBoxFromRadius(latitude, longitude, radius)
    }

    /**
     * When changing category we need to re-validate the view type to ensure it is allowed on that category
     */
    const validatedViewType = getSearchViewType({
      categoryId: selectedCategoryId,
      isRadiusLocation: !!location.area,
      isGridViewEnabled: isSRPGridToggleEnabled,
      isRealEstateGridExpEnabled: !!isRealEstateGridExpTestGroup,
      queryParamView: viewType,
    })

    refetchResults(
      {
        view: validatedViewType,
        categoryId: selectedCategoryId,
        offset: 0,
        location: { id: location.id, area: location.area, boundingBox },
      },
      {
        event: {
          action: GA_EVENT.CategorySelect,
          label: trackingLabel,
        },
        forceNavigateUrl: categoryLeaf.seoUrl || undefined,
      }
    )

    if (validatedViewType !== viewType) {
      updatePageViewType(validatedViewType)
    }
  }

  const handleAccordionClick = (id: string) => {
    trackEvent({ action: GA_EVENT.CategorySelectBegin })
    onClickAccordionItem?.(id)
  }

  if (!categoryTree) return null

  return (
    <AccordionItem
      id="categorySection"
      onClick={handleAccordionClick}
      title={t('filters.category.title')}
      description={description}
    >
      <TreeFilter
        attributes={categoryTree}
        handleSelectAttribute={handleSelectCategory}
        selectedId={categoryId}
        shouldSelectLeaf
        type="category"
      />
    </AccordionItem>
  )
}
